import { useCallback, useEffect, DependencyList } from 'react';

import { DebouncedState, useDebouncedCallback } from 'use-debounce';


let lockingCallbacks = new Set<(locked: boolean) => void>()
let errorCallbacks = new Set<(reason: unknown) => void>()
let callChainPromise = Promise.resolve()
let debouncedCallbacks = new Set<DebouncedState<() => void>>

const reportLocked = (locked: boolean) => {
    lockingCallbacks.forEach(callback => callback(locked))
}

export const reportError = (reason: unknown) => {
    errorCallbacks.forEach(callback => callback(reason))
}

const handleError = (reason: unknown) => {
    reportError(reason)
    callChainPromise = Promise.resolve()
    throw "cancel"
}

// Flush every pending debounced save now. Used before any chained action (below) and on hard
// page-unload (bottom of file).
const flushAllDebounced = () => {
    debouncedCallbacks.forEach(callback => callback.flush())
}

export const addToCallChain = (func: () => void | PromiseLike<void>, lock: boolean) => {
    if (lock) {
        reportLocked(true)
    }
    flushAllDebounced()
    addToCallChainSimple(func)
    callChainPromise = callChainPromise.finally(
        () => {
            if (lock) {
                reportLocked(false)
            }
        }
    )
}

const addToCallChainSimple = (func: () => void | PromiseLike<void>) => {
    callChainPromise = callChainPromise.then(func).catch(handleError)
}

export const useRegisteredLockingCallback = (setLocked: (locked: boolean) => void) => {
    useEffect(
        () => {
            lockingCallbacks.add(setLocked)
            return () => {lockingCallbacks.delete(setLocked)}
        },
        [setLocked]
    )
}

export const useRegisteredErrorCallback = (handleError: (reason: unknown) => void) => {
    useEffect(
        () => {
            errorCallbacks.add(handleError)
            return () => {errorCallbacks.delete(handleError)}
        },
        [handleError]
    )
}

export const useChainedCallback = (func: () => void | PromiseLike<void>, lock: boolean, dependencies: DependencyList) => 
    useCallback(() => addToCallChain(func, lock), dependencies)

export const useDebouncedChainedCallback = (func: () => void | PromiseLike<void>, wait?: number): DebouncedState<() => void> => {
    const debouncedCallback = useDebouncedCallback(() => addToCallChainSimple(func), wait)
    useEffect(
        () => {
            debouncedCallbacks.add(debouncedCallback)
            return () => {
                debouncedCallback.flush()
                debouncedCallbacks.delete(debouncedCallback)
            }
        },
        [debouncedCallback]
    )
    return debouncedCallback
}

// React unmount cleanup doesn't run on a hard browser close / navigation, so flush pending
// debounced saves here too. The saves' mutations use keepalive fetch (Api.doFetch), so the
// requests still complete after the page goes away. Harmless if nothing is pending.
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', flushAllDebounced)
}
